import { useEffect, useState } from "react";
import { useSavedBrandTemplates } from "../hooks/useSavedBrandTemplates";
import { useSavedBrandTemplateSwatches } from "../hooks/useSavedBrandTemplateSwatches";
import type { SavedBrandTemplate } from "../services/brandTemplatesApi";
import type { MyBrandTemplate } from "../services/myBrandTemplatesStorage";
import { requestBrandTemplatePreview } from "../../asset-library/services/brandTemplatePreviewHandoff";
import styles from "./ApplyBrandPanel.module.css";

/** Rows shown before "Show all N matching templates" — keeps a long org list
 *  from pushing My Templates (and the apply controls below) off-screen. */
const VISIBLE_TEMPLATES_LIMIT = 5;

/** Extraction state of the currently-selected row, as seen from this tab —
 *  "idle" covers both "nothing selected yet" and "a different tab owns the
 *  in-flight/most-recent extraction". */
export type SavedTemplateSelectionStatus = "idle" | "extracting" | "ready" | "error";

interface SavedTemplatesTabProps {
  readonly selectedId: string | null;
  readonly disabled: boolean;
  readonly selectedStatus: SavedTemplateSelectionStatus;
  readonly onSelect: (template: SavedBrandTemplate) => void;
  readonly onReview: () => void;
  readonly myTemplates: readonly MyBrandTemplate[];
  readonly onSelectMyTemplate: (template: MyBrandTemplate) => void;
  readonly onRemoveMyTemplate: (id: string) => void;
}

/** Saved Templates tab: org-managed brand presets from blob storage (see
 *  brandTemplatesApi.ts) — search + brand filter above a single-select list —
 *  followed by "My Templates", the user's own locally-saved uploads (see
 *  myBrandTemplatesStorage.ts). The list stays visible after selection; the
 *  "will be applied" note + Review & Customise render inline under the
 *  selected row, in either section.
 *
 *  An org row's eye icon opens the Asset Library's Brand Templates detail
 *  view for that same template — not an inline copy here — since that's
 *  where the real preview UI (and its own "Use this template" action) lives;
 *  see brandTemplatePreviewHandoff.ts for how the request reaches it. */
export function SavedTemplatesTab({
  selectedId, disabled, selectedStatus, onSelect, onReview, myTemplates, onSelectMyTemplate, onRemoveMyTemplate,
}: Readonly<SavedTemplatesTabProps>) {
  const { loadState, filteredTemplates, templates, brandOptions, search, setSearch, brandFilter, setBrandFilter, retry } =
    useSavedBrandTemplates();
  const [showAll, setShowAll] = useState(false);
  // A new search/filter narrows a different set of matches — collapse back to
  // the top 5 rather than carrying over an earlier "Show all".
  useEffect(() => {
    setShowAll(false);
  }, [search, brandFilter]);
  // A pick made elsewhere (the Asset Library's Brand Templates tab routes in
  // with `selectedId` already set — see pendingSavedTemplateSelection.ts) can
  // land past the top 5. Expand so the selected row is actually visible
  // instead of silently hiding it behind "Show all" — this never fires for a
  // selection made IN this list, since a hidden row can't be clicked.
  useEffect(() => {
    if (!selectedId) return;
    const index = filteredTemplates.findIndex((t) => t.id === selectedId);
    if (index >= VISIBLE_TEMPLATES_LIMIT) setShowAll(true);
  }, [selectedId, filteredTemplates]);
  const visibleTemplates = showAll ? filteredTemplates : filteredTemplates.slice(0, VISIBLE_TEMPLATES_LIMIT);

  return (
    <div className={styles.sourcePane}>
      {loadState.status === "loading" && <div className={styles.savedTemplatesStatus}>Loading saved templates…</div>}

      {loadState.status === "error" && (
        <div className={`${styles.statusBox} ${styles.statusError}`}>
          <span className={styles.statusLabel}>Couldn&rsquo;t load saved templates:</span>
          {loadState.message}
          <button type="button" className={styles.reviewBtn} onClick={retry}>
            Retry
          </button>
        </div>
      )}

      {loadState.status === "ready" && (
        <>
          <div className={styles.savedTemplatesHeader}>
            <span className={styles.savedTemplatesTitle}>
              SAVED TEMPLATES{" "}
              <span aria-hidden="true" title="Managed by your organisation">🔒</span>
            </span>
            <span className={styles.savedTemplatesCount}>{templates.length} template{templates.length === 1 ? "" : "s"}</span>
          </div>

          <div className={styles.savedTemplatesToolbar}>
            <input
              type="search"
              className={styles.savedTemplatesSearch}
              placeholder="Search templates"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search saved templates"
            />
            <select
              className={styles.savedTemplatesBrandSelect}
              value={brandFilter ?? ""}
              onChange={(e) => setBrandFilter(e.target.value || null)}
              aria-label="Filter by brand"
            >
              <option value="">All brands</option>
              {brandOptions.map((brand) => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>

          {filteredTemplates.length === 0 ? (
            <div className={styles.savedTemplatesStatus}>
              {templates.length === 0 ? "No saved templates yet." : "No saved templates match your search."}
            </div>
          ) : (
            <>
              <ul className={styles.savedTemplatesList}>
                {visibleTemplates.map((template) => (
                  <SavedTemplateRow
                    key={template.id}
                    template={template}
                    selected={template.id === selectedId}
                    status={template.id === selectedId ? selectedStatus : "idle"}
                    disabled={disabled}
                    onSelect={() => onSelect(template)}
                    onReview={onReview}
                    onPreview={() => requestBrandTemplatePreview(template.id)}
                  />
                ))}
              </ul>
              {!showAll && filteredTemplates.length > VISIBLE_TEMPLATES_LIMIT && (
                <button type="button" className={styles.showAllTemplatesBtn} onClick={() => setShowAll(true)}>
                  Show all {filteredTemplates.length} matching templates
                </button>
              )}
            </>
          )}
        </>
      )}

      {myTemplates.length > 0 && (
        <div className={styles.myTemplatesSection}>
          <div className={styles.savedTemplatesHeader}>
            <span className={styles.savedTemplatesTitle}>MY TEMPLATES</span>
          </div>
          <p className={styles.myTemplatesSubtitle}>Saved by you from uploads. Yours to manage, not your organisation.</p>
          <ul className={styles.savedTemplatesList}>
            {myTemplates.map((template) => (
              <MyTemplateRow
                key={template.id}
                template={template}
                selected={template.id === selectedId}
                status={template.id === selectedId ? selectedStatus : "idle"}
                disabled={disabled}
                onSelect={() => onSelectMyTemplate(template)}
                onRemove={() => onRemoveMyTemplate(template.id)}
                onReview={onReview}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const REVIEW_BUTTON_LABEL: Record<SavedTemplateSelectionStatus, string> = {
  idle: "Review & Customise",
  extracting: "Extracting…",
  ready: "Review & Customise",
  error: "Extraction failed",
};

/** Row-preview swatches shared by both the org and My Templates rows. */
function RowSwatches({ colors }: Readonly<{ colors: readonly string[] }>) {
  if (colors.length === 0) return null;
  return (
    <span className={styles.themeCardSwatches}>
      {colors.map((hex) => (
        <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} title={`#${hex}`} />
      ))}
    </span>
  );
}

/** The "will be applied"/error note + Review & Customise button shown under
 *  a selected row — identical for org and My Templates rows. */
function RowSelectedNote({ name, status, onReview }: Readonly<{
  name: string;
  status: SavedTemplateSelectionStatus;
  onReview: () => void;
}>) {
  return (
    <div className={styles.selectedNote}>
      {status === "error" ? (
        <>Couldn&rsquo;t load <b>{name}</b> — pick another template, or try selecting it again.</>
      ) : (
        <><b>{name}</b> will be applied with its colours, fonts and styles.</>
      )}
      <div>
        <button type="button" className={styles.reviewBtn} onClick={onReview} disabled={status !== "ready"}>
          {REVIEW_BUTTON_LABEL[status]}
        </button>
      </div>
    </div>
  );
}

function SavedTemplateRow({ template, selected, status, disabled, onSelect, onReview, onPreview }: Readonly<{
  template: SavedBrandTemplate;
  selected: boolean;
  status: SavedTemplateSelectionStatus;
  disabled: boolean;
  onSelect: () => void;
  onReview: () => void;
  onPreview: () => void;
}>) {
  const swatches = useSavedBrandTemplateSwatches(template);
  // Shown but not selectable if there's no colour source at all.
  const selectable = template.themeColors !== null || template.sourceFileUrl !== null;

  return (
    <li className={`${styles.savedTemplateRow} ${selected ? styles.savedTemplateRowSelected : ""}`}>
      <div className={styles.savedTemplateRowTop}>
        <label className={styles.savedTemplateRowMain}>
          <input
            type="radio"
            name="saved-brand-template"
            className={styles.savedTemplateRadio}
            checked={selected}
            disabled={disabled || !selectable}
            onChange={onSelect}
          />
          <span className={styles.savedTemplateName}>{template.name}</span>
          <RowSwatches colors={swatches} />
          {!selectable && <span className={styles.previewOnlyBadge}>Preview only</span>}
        </label>
        <div className={styles.savedTemplateEyeCell}>
          <button type="button" className={styles.savedTemplateEye} aria-label={`Preview ${template.name}`} title="Preview" onClick={onPreview}>
            👁
          </button>
        </div>
      </div>

      {selected && <RowSelectedNote name={template.name} status={status} onReview={onReview} />}
    </li>
  );
}

/** A locally-saved (My Templates) row — same selection/note/Review &
 *  Customise treatment as SavedTemplateRow, but its swatches come straight
 *  off the entry (no download to fall back to) and it offers a Remove (×)
 *  action instead of the org list's preview eye. */
function MyTemplateRow({ template, selected, status, disabled, onSelect, onRemove, onReview }: Readonly<{
  template: MyBrandTemplate;
  selected: boolean;
  status: SavedTemplateSelectionStatus;
  disabled: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onReview: () => void;
}>) {
  return (
    <li className={`${styles.savedTemplateRow} ${selected ? styles.savedTemplateRowSelected : ""}`}>
      <div className={styles.savedTemplateRowTop}>
        <label className={styles.savedTemplateRowMain}>
          <input
            type="radio"
            name="saved-brand-template"
            className={styles.savedTemplateRadio}
            checked={selected}
            disabled={disabled}
            onChange={onSelect}
          />
          <span className={styles.savedTemplateName}>{template.name}</span>
          <RowSwatches colors={template.colors} />
        </label>
        <div className={styles.savedTemplateEyeCell}>
          <button
            type="button"
            className={styles.myTemplateRemoveBtn}
            aria-label={`Remove ${template.name}`}
            title="Remove"
            disabled={disabled}
            onClick={onRemove}
          >
            ×
          </button>
        </div>
      </div>

      {selected && <RowSelectedNote name={template.name} status={status} onReview={onReview} />}
    </li>
  );
}
